import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "src/components/ui/breadcrumb";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Avatar, Spin, Tooltip } from "antd";
import { cn, DateFormate } from "../../lib/utils";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../lib/api";
import { CourseCard } from "../../components/CourseCard";
import axios from "axios";
import { toast } from "sonner";
import { useAPI } from "../../context/APIContext";
import LoaderPage from "../../components/LoaderPage";

export default function MainTeacherInfo() {
  const location = useLocation();
  const teacher = location.state?.teacher;

  const { coursesItems } = useAPI();
  const { loadingCourses, courses, getAllCourses } = coursesItems;

  const teacherCourses = courses.filter(
    (course) => course.created_by.id === teacher.id
  );

  const [loadingTeacherData, setLoadingTeacherData] = useState(true);
  const [teacherData, setTeacherData] = useState([]);
  const getAllTeacherData = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/teacher-profiles/${teacher.id}`
      );
      setTeacherData(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingTeacherData(false);
    }
  };

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/reviews/teacher/${teacher.id}`
      );
      setReviews(data.data);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast("Error", { description: msg, status: "error" });
    } finally {
      setLoadingReviews(false);
    }
  };

  useEffect(() => {
    getAllTeacherData();
    fetchReviews();
    window.scroll(0, 0);
  }, []);

  if (loadingTeacherData || loadingCourses) {
    return <LoaderPage title={"Loading Data"} />;
  }
  return (
    <div className="space-y-20 max-w-7xl mx-auto px-4 mt-14">
      <div className="space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">الصفحة الرئيسية</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/teachers">المعلمون</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbPage className="font-semibold">
                {teacherData.user?.name}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="gap-4 text-center flex flex-col items-center justify-center">
          <Avatar
            className={cn(
              "min-w-32 min-h-32 rounded-full flex items-center justify-center text-xs font-medium",
              "bg-blue-100 text-blue-600",
              "dark:bg-blue-900 dark:text-blue-300 text-lg"
            )}
            src={`${UrlImage}${teacherData.user?.image}`}
          >
            {teacherData.user?.name?.split(" ")[0]}
          </Avatar>
          <div className="text-2xl font-bold">{teacherData.user?.name}</div>
          <p className="text-base text-muted-foreground">أستاذة لغة عربية</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="font-bold text-xl border-b pb-4">عني:</div>
        <div className="text-sm text-muted-foreground">
          {teacherData.description || "لا يوجد تعريف"}
        </div>
      </div>

      <div className="space-y-6">
        <div className="font-bold text-xl border-b pb-4">
          عدد الطلاب المشتركون :
        </div>
        <Avatar.Group
          max={{
            count: 5,
            style: {
              color: "#2563eb",
              backgroundColor: "#dbeafe",
              width: "2.5rem",
              height: "2.5rem",
            },
            popover: {
              placement: "top",
              overlayStyle: {
                zIndex: 99999,
                pointerEvents: "auto",
              },
              overlayClassName: "avatar-overflow-row",
            },
          }}
        >
          {[1, 2, 3, 4, 5, 6].map(({ user }) => {
            const url =
              user?.role === "student"
                ? "/admin/settings"
                : user?.role === "teacher" && "/admin/teacher-settings";

            return (
              <Tooltip title={user?.name} placement="top">
                <Link to={url} state={{ userData: user }}>
                  <Avatar
                    src={`${UrlImage}${user?.image}`}
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium",
                      "bg-blue-100 text-blue-600",
                      "dark:bg-blue-900 dark:text-blue-300"
                    )}
                  >
                    {user?.name?.substring(0, 2).toUpperCase()}
                  </Avatar>
                </Link>
              </Tooltip>
            );
          })}
        </Avatar.Group>
      </div>

      <div className="space-y-6">
        <div className="font-bold text-xl border-b pb-4">
          الدورات المرتبطة بالمعلم :
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teacherCourses.length !== 0 ? (
            teacherCourses.map((item, index) => (
              <CourseCard
                key={index}
                item={item}
                btnTitle={"التسجيل الآن"}
                // onClick={() =>
                //   navigate(`/course-details`, { state: { course: item } })
                // }
              />
            ))
          ) : (
            <div className="text-2xl md:text-4xl text-center font-bold h-40 flex items-center justify-center drop-shadow-md col-span-3">
              لا يوجد دورات لهذا المدرس 😪
            </div>
          )}
        </div>
      </div>

      {/* Review */}
      {reviews.length !== 0 && (
        <div className="space-y-6">
          <h2 className="font-bold text-2xl">
            بعض آراء الطلاب :{" "}
            <span className="text-sm text-muted-foreground">
              ({reviews.length} تقيمات)
            </span>
          </h2>
          {loadingReviews ? (
            <div className="flex items-center justify-center w-full h-36">
              <Spin size="large" />
            </div>
          ) : (
            reviews.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <StudentComments
                  avatar={item?.image}
                  name={item?.name}
                  comment={item?.message}
                  timestamp={DateFormate(item?.created_at)}
                  rating={item?.rate ? Number(item?.rate) : undefined}
                />
              </motion.div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const StudentComments = ({ avatar, name, comment, timestamp, rating }) => {
  return (
    <div className="flex items-center gap-2">
      <Avatar
        className={cn(
          "min-w-12 min-h-12 rounded-full flex items-center justify-center text-xs font-medium",
          "bg-blue-100 text-blue-600",
          "dark:bg-blue-900 dark:text-blue-300"
        )}
        src={`${UrlImage}${avatar}`}
      >
        {name?.charAt(0)}
      </Avatar>
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center gap-4">
          <p className="text-xs font-bold">{name}</p>
          <div className="flex items-center gap-1 text-sm">
            <Star className={`w-4 h-4 text-yellow-400 fill-yellow-400`} />
            {rating}
          </div>
        </div>
        <div className="bg-secondary w-full py-3 px-6 rounded-xl">
          <p className="font-medium text-base md:text-lg">{comment}</p>
        </div>
        <p className="text-xs font-medium text-muted-foreground">{timestamp}</p>
      </div>
    </div>
  );
};
